import React, { useState } from "react";
import { Plus, X, Check } from "lucide-react";

const sanitize = (s) => (s || "").replace(/\s+/g, " ").trim().slice(0, 40);

export function ChipMultiSelect({
  options = [], value = [], onChange, singleSelect = false, allowOther = true,
  testid = "chips", hint, placeholder = "Add your own…", max = 40,
}) {
  const [search, setSearch] = useState("");
  const [showOther, setShowOther] = useState(false);
  const [custom, setCustom] = useState("");

  const labels = options.map((o) => o.label);
  const selected = new Set(value);
  const customChips = value.filter((v) => !labels.includes(v));
  const showSearch = options.length > 14;
  const visible = showSearch && search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (label) => {
    if (label.toLowerCase() === "other") { setShowOther((v) => !v); return; }
    if (singleSelect) { onChange([label]); return; }
    if (selected.has(label)) onChange(value.filter((v) => v !== label));
    else if (value.length < max) onChange([...value, label]);
  };

  const addCustom = () => {
    const c = sanitize(custom);
    if (!c) return;
    if (value.some((v) => v.toLowerCase() === c.toLowerCase())) { setCustom(""); return; }
    if (singleSelect) onChange([c]);
    else if (value.length < max) onChange([...value, c]);
    setCustom(""); setShowOther(false);
  };

  const removeChip = (label) => onChange(value.filter((v) => v !== label));

  return (
    <div data-testid={testid}>
      {hint && <p className="text-[11px] text-[#9E978E] mb-2">{hint}</p>}
      {showSearch && (
        <input data-testid={`${testid}-search`} value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search options…"
          className="w-full mb-2 rounded-lg bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-3 py-2 text-sm text-[#FBF8F3] placeholder:text-[#9E978E]" />
      )}
      <div className="flex flex-wrap gap-2">
        {visible.map((o) => {
          const isOther = o.label.toLowerCase() === "other";
          const active = !isOther && selected.has(o.label);
          return (
            <button key={o.id} type="button" data-testid={`${testid}-opt-${o.id}`} onClick={() => toggle(o.label)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-colors ${
                active
                  ? "bg-[#541224] border-[#D4AF37] text-[#FBF8F3]"
                  : "bg-[#1C171A] border-white/10 text-[#D8D2C9] hover:border-[#D4AF37]/50"
              } ${isOther ? "border-dashed border-[#D4AF37]/50 text-[#D4AF37]" : ""}`}>
              {active && <Check size={13} />}{isOther && <Plus size={13} />}{o.label}
            </button>
          );
        })}
        {customChips.map((c) => (
          <span key={c} data-testid={`${testid}-custom-${c}`}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border bg-[#541224] border-[#D4AF37] text-[#FBF8F3]">
            {c}
            <button type="button" aria-label="remove" onClick={() => removeChip(c)} className="text-[#E58CA0] hover:text-white"><X size={13} /></button>
          </span>
        ))}
      </div>
      {allowOther && showOther && (
        <div className="flex items-center gap-2 mt-2">
          <input data-testid={`${testid}-other-input`} value={custom} maxLength={40}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder={placeholder}
            className="flex-1 rounded-lg bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-3 py-2 text-sm text-[#FBF8F3] placeholder:text-[#9E978E]" />
          <button type="button" data-testid={`${testid}-other-add`} onClick={addCustom}
            className="rounded-lg bg-[#541224] border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-2 text-sm hover:border-[#D4AF37]">Add</button>
        </div>
      )}
      {!singleSelect && <p className="text-[10px] text-[#9E978E] mt-2">{value.length} selected</p>}
    </div>
  );
}
